import React, { useEffect, useState } from "react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useGroupView } from "../context/GroupViewContext";
import { FRAMEWORK_LABELS } from "../context/FrameworksContext";
import { RepeatFindingsPanel } from "../components/RepeatFindingsPanel";

// Group overview — the parent organisation's view of every member. Reads
// need group:view; the consolidated figures need group:admin. Membership is
// the consultancy's to change.

export interface GroupMember {
  id: string;
  name: string;
  sector: string | null;
  size: string | null;
  dpTier: string | null;
  tier: string;
  onboardingComplete: boolean;
  frameworks: string[];
  latestScore: number | null;
  latestCheckAt: string | null;
  userCount: number;
  siteCount: number;
  openRisks: number;
  soa: Record<string, number>;
}

export interface GroupPayload {
  id: string;
  name: string;
  parentOrganizationId: string;
  organizations: Array<{ id: string; name: string; isParent: boolean }>;
  consolidatedHeadcount: number | null;
  consolidatedTurnoverAmount: number | null;
  consolidatedTurnoverCurrency: string | null;
  members: GroupMember[] | null;
  canManage: boolean;
}

const CURRENCIES = ["GBP", "EUR", "USD", "CHF", "AUD", "CAD", "SGD", "HKD", "JPY", "NOK", "SEK", "DKK"];

function scoreColour(score: number | null): string {
  if (score === null) return theme.color.muted;
  if (score >= 80) return theme.color.successText;
  if (score >= 50) return theme.color.warningText;
  return theme.color.dangerText;
}

function soaLine(soa: Record<string, number>): string {
  const total = Object.values(soa).reduce((a, b) => a + b, 0);
  if (!total) return "—";
  const compliant = soa.COMPLIANT ?? 0;
  const gaps = soa.GAP ?? 0;
  return `${compliant}/${total} compliant${gaps ? ` · ${gaps} gap${gaps === 1 ? "" : "s"}` : ""}`;
}

function ConsolidatedFigures({ group, onSaved }: { group: GroupPayload; onSaved: (g: GroupPayload) => void }) {
  const [headcount, setHeadcount] = useState(group.consolidatedHeadcount != null ? String(group.consolidatedHeadcount) : "");
  const [amount, setAmount]       = useState(group.consolidatedTurnoverAmount != null ? String(group.consolidatedTurnoverAmount) : "");
  const [currency, setCurrency]   = useState(group.consolidatedTurnoverCurrency ?? "GBP");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState("");

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSaving(true); setSaved(false);
    const h = headcount.trim() === "" ? null : Number(headcount);
    const a = amount.trim() === "" ? null : Number(amount);
    if ((h !== null && (!Number.isInteger(h) || h < 0)) || (a !== null && (!Number.isFinite(a) || a < 0))) {
      setError("Enter whole, non-negative numbers."); setSaving(false); return;
    }
    try {
      const r = await api.patch<{ group: GroupPayload }>("/groups/mine", {
        consolidatedHeadcount: h,
        consolidatedTurnoverAmount: a,
        consolidatedTurnoverCurrency: a === null ? null : currency,
      });
      onSaved({ ...group, ...r.group });
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const label: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
  return (
    <form onSubmit={save} style={{ ...card, marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Consolidated group figures</div>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: theme.color.subtle, maxWidth: 640, lineHeight: 1.5 }}>
        Headcount and turnover of the whole group. NIS2, DORA and the UK NIS Regulations size an organisation by its linked
        enterprises together (Recommendation 2003/361), so a small subsidiary of a large group is treated as large. Data protection
        duties are unaffected — they stay with each legal entity.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 140px" }}>
          <label style={label}>Group headcount</label>
          <input style={inputStyle} value={headcount} inputMode="numeric" disabled={!group.canManage} onChange={(e) => { setHeadcount(e.target.value); setSaved(false); }} placeholder="e.g. 5000" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 160px" }}>
          <label style={label}>Group turnover</label>
          <input style={inputStyle} value={amount} inputMode="decimal" disabled={!group.canManage} onChange={(e) => { setAmount(e.target.value); setSaved(false); }} placeholder="Annual, whole units" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Currency</label>
          <select style={inputStyle} value={currency} disabled={!group.canManage} onChange={(e) => { setCurrency(e.target.value); setSaved(false); }}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {group.canManage && (
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        )}
        {saved && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
        {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
      </div>
    </form>
  );
}

export function GroupOverviewPage() {
  const { user } = useCurrentUser();
  const groupView = useGroupView();
  const onOpenOrganisation = groupView.available ? groupView.setViewingOrg : undefined;
  const [group, setGroup]     = useState<GroupPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ group: GroupPayload | null }>("/groups/mine").then((d) => setGroup(d.group)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-pad" style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;
  if (!group || !group.members) {
    return (
      <div className="page-pad" style={{ fontFamily: theme.font }}>
        <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Group overview</h1>
        <p style={{ fontSize: 13, color: theme.color.subtle }}>
          {group ? `This organisation is part of ${group.name}. The group overview is available from the parent organisation.` : "This organisation is not part of a group. Your consultancy can link a parent organisation and its subsidiaries."}
        </p>
      </div>
    );
  }

  const members = group.members;
  const scored = members.filter((m) => m.latestScore !== null);
  const avg = scored.length ? Math.round(scored.reduce((s, m) => s + (m.latestScore ?? 0), 0) / scored.length) : null;
  const totalRisks = members.reduce((s, m) => s + m.openRisks, 0);
  const totalPeople = members.reduce((s, m) => s + m.userCount, 0);

  const cell: React.CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top" };
  const th: React.CSSProperties = { ...cell, ...text.eyebrow, borderBottom: "1px solid var(--c-line)" };

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>{group.name}</h1>
        <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle, maxWidth: 680 }}>
          Every organisation in the group, each with its own management system, certificates and data protection duties.
          {group.canManage ? " You can open any of them and work there with your usual permissions." : " You can open any of them to read."}
          {" "}Membership is managed by your consultancy.
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Organisations", value: members.length },
          { label: "Average score", value: avg === null ? "—" : `${avg}%` },
          { label: "Open risks", value: totalRisks },
          { label: "Members", value: totalPeople },
        ].map((s) => (
          <div key={s.label} style={{ ...card, padding: "12px 16px", minWidth: 140, flex: "1 1 140px" }}>
            <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginTop: 2 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: "auto", marginBottom: 20 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr style={{ background: theme.color.panelAlt }}>
              <th style={th}>Organisation</th>
              <th style={th}>Standards</th>
              <th style={th}>Score</th>
              <th style={th}>SOA</th>
              <th style={th}>Open risks</th>
              <th style={th}>People · sites</th>
              {onOpenOrganisation && <th style={{ ...th, width: 90 }} />}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isParent = m.id === group.parentOrganizationId;
              const isOwn = m.id === user?.organizationId;
              return (
                <tr key={m.id}>
                  <td style={cell}>
                    <div style={{ fontWeight: 600, color: theme.color.heading }}>
                      {m.name}
                      {isParent && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: theme.color.accentText, background: theme.color.accentBg, padding: "2px 6px", borderRadius: 4 }}>Parent</span>}
                    </div>
                    <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                      {[m.sector, m.size ? `${m.size} staff` : null, m.tier].filter(Boolean).join(" · ") || "—"}
                      {!m.onboardingComplete && " · onboarding incomplete"}
                    </div>
                  </td>
                  <td style={cell}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {m.frameworks.length === 0 && <span style={{ color: theme.color.muted }}>—</span>}
                      {m.frameworks.map((f) => (
                        <span key={f} style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: theme.color.panelAlt, color: theme.color.text }}>
                          {(FRAMEWORK_LABELS as Record<string, string>)[f] ?? f}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ ...cell, fontWeight: 700, color: scoreColour(m.latestScore) }}>
                    {m.latestScore === null ? "—" : `${m.latestScore}%`}
                    {m.latestCheckAt && <div style={{ fontSize: 10, fontWeight: 400, color: theme.color.subtle }}>{new Date(m.latestCheckAt).toLocaleDateString("en-GB")}</div>}
                  </td>
                  <td style={{ ...cell, fontSize: 12 }}>{soaLine(m.soa)}</td>
                  <td style={{ ...cell, color: m.openRisks ? theme.color.warningText : theme.color.muted }}>{m.openRisks}</td>
                  <td style={{ ...cell, fontSize: 12, color: theme.color.muted }}>{m.userCount} · {m.siteCount}</td>
                  {onOpenOrganisation && (
                    <td style={{ ...cell, textAlign: "right" }}>
                      <button
                        onClick={() => onOpenOrganisation(isOwn ? null : m.id)}
                        style={{ padding: "3px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer" }}
                      >
                        Open
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RepeatFindingsPanel scope="group" />

      <ConsolidatedFigures group={group} onSaved={setGroup} />
    </div>
  );
}
